import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from '@blocs.i18n';
import { BasicContainer, BodyText, BodyTextSmall, FlexCol, FlexRow, PaywallButton } from '@minecraft.atoms';
import { useModal } from '@minecraft.modal';
import { usePermissions } from '@minecraft.permissions';
import { useNewFeature } from '@blocs.features-experiments';
import { SocialMediaButton } from '@minecraft.molecules';
import { helpers, useDeviceAndBrowserInfo } from '@minecraft.utils';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { AppType } from '@blocs.common';
import { SocialMediaPlatform } from '@minecraft.graphql-operations';
import { InstagramConsentModal, InstagramDisconnectModal } from '@minecraft.organisms';
import { PaywallLocation, useBillingPopup } from '@blocs.paywall';
import { useProfileViewContext } from '../ProfileViewContext';

const PROFILE_VISIT_COUNT = 'profileVisitCount';

const Badge = () => {
  const { t } = useTranslation();
  return (
    <BasicContainer
      borderRadius="md"
      padding={1}
      borderPlacement="all"
      borderColor="hotTrialUpsellPaywall"
      borderSize={0.1}
      data-testid="new-badge"
    >
      <BodyTextSmall fontWeight="semibold" color="hotTrialPaywall">
        {t('common:profile.badge.label')}
      </BodyTextSmall>
    </BasicContainer>
  );
};

type ReadonlyInstagramBtn = {
  readOnly: true;
  label: string;
  profileLink: string;
  onConnect?: never;
  onDisconnect?: never;
  showCloseButton?: never;
};

type WriteableInstagramBtn = {
  readOnly?: false;
  label: string;
  profileLink: string;
  onConnect: () => void;
  onDisconnect: () => void;
  showCloseButton?: boolean;
};

interface InstagramButtonClick {
  profileLink: string;
  onConnect: () => void;
  readOnly: boolean;
  isCasting: boolean;
  deviceInfo: string;
  browserInfo: string;
}

const handleInstagramButtonClick = ({
  profileLink,
  onConnect,
  readOnly,
  isCasting,
  deviceInfo,
  browserInfo,
}: InstagramButtonClick) => {
  if (profileLink) {
    window.open(profileLink, '_blank', 'noopener,noreferrer');
    if (isCasting) {
      const data = {
        timestamp: new Date().toISOString(),
        device: deviceInfo,
        browser: browserInfo,
      };
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.CD_INSTAGRAM_CLICK_TALENT_PROFILE,
        data,
      });
    }
  } else if (!readOnly) {
    onConnect();
  }
};

type InstagramButtonProps = ReadonlyInstagramBtn | WriteableInstagramBtn;

const InstagramButton = ({
  label,
  profileLink,
  onConnect,
  onDisconnect,
  showCloseButton = false,
  readOnly = false,
}: InstagramButtonProps) => {
  const { appType } = useProfileViewContext();
  const isCasting = appType === AppType.Casting;
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();
  return (
    <SocialMediaButton
      id="connect-instagram"
      label={label}
      aria-label={label}
      iconName="profile-social-instagram"
      isLarge
      wrapperClassName="cn_atom_p-1"
      onClick={() =>
        handleInstagramButtonClick({ profileLink, onConnect, readOnly, isCasting, deviceInfo, browserInfo })
      }
      onCloseClick={showCloseButton ? onDisconnect : undefined}
    />
  );
};

const SocialButtonContainer: FC = ({ children }) => (
  <FlexRow
    alignItems="center"
    justifyContent="center"
    bgColor="cardLightContrast"
    borderRadius="md"
    className="cn_atom_p-2"
    mdProps={{ w: 'max', className: 'cn_atom_p-2' }}
  >
    {children}
  </FlexRow>
);

const Preview = ({
  isInstagramConnected,
  followerCount,
  profileLink,
}: {
  isInstagramConnected: boolean;
  followerCount: number;
  profileLink: string;
}) => {
  const { t } = useTranslation();
  if (!isInstagramConnected) return null;

  return (
    <BasicContainer
      data-testid="my-socials-container-preview"
      borderPlacement="bottom"
      className="cn_atom_py-4 cn_mt-2"
    >
      <FlexCol gap="2">
        <FlexRow alignItems="center" gap="2">
          <BodyText as="h2" fontWeight="semibold">
            {t('common:profile.info.preview.socialLinks')}
          </BodyText>
        </FlexRow>
        <SocialButtonContainer>
          <InstagramButton
            label={t('common:profile.info.social.followerCount', {
              formattedCount: helpers.formatMetricCount(followerCount, true),
            })}
            readOnly
            profileLink={profileLink}
          />
        </SocialButtonContainer>
      </FlexCol>
    </BasicContainer>
  );
};

type SocialConnectSectionProps = {
  isNewFeature: boolean;
  instagramButtonLabel: string;
  profileLink: string;
  hasWriteAccess: boolean;
  toggleModal: () => void;
  setFeatureIsOld: () => void;
  toggleDisconnectModal: () => void;
};

const SocialConnectSection = ({
  isNewFeature,
  instagramButtonLabel,
  profileLink,
  hasWriteAccess,
  toggleModal,
  setFeatureIsOld,
  toggleDisconnectModal,
}: SocialConnectSectionProps) => {
  const { t } = useTranslation();
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();

  return (
    <BasicContainer data-testid="my-socials-container" borderPlacement="bottom" className="cn_atom_pb-4 cn_mt-2">
      <FlexRow alignItems="center" gap="2">
        <BodyText as="h2" fontWeight="semibold">
          {t('common:profile.info.mySocial')}
        </BodyText>
        {isNewFeature && <Badge />}
      </FlexRow>
      <BasicContainer mt="2">
        <BodyText>{t('common:profile.info.mySocial.description')}</BodyText>
      </BasicContainer>
      <BasicContainer mt="2">
        {hasWriteAccess ? (
          <SocialButtonContainer>
            <InstagramButton
              label={instagramButtonLabel}
              profileLink={profileLink}
              onConnect={() => {
                toggleModal();
                setFeatureIsOld();
              }}
              onDisconnect={toggleDisconnectModal}
              showCloseButton={!!profileLink}
            />
          </SocialButtonContainer>
        ) : (
          <PaywallButton
            onClick={() => {
              toggleModal();
              setFeatureIsOld();
              captureHeapEvent({
                name: HEAP_EVENT_NAMES.TALENT_SOCIAL_CLICK_UPGRADE_TO_PREMIUM,
                data: {
                  timestamp: new Date().toISOString(),
                  device: deviceInfo,
                  browser: browserInfo,
                  location: 'social-upgrade',
                },
              });
            }}
            label={{ name: t('common:membership.upgradeToPremiumShort'), justify: 'start' }}
            startIcon={{ name: 'rocket', justify: 'end' }}
            color="hotTrial"
            isSmall
            fixedWidth
            data-testid="consent-modal-button"
          />
        )}
      </BasicContainer>
    </BasicContainer>
  );
};

interface MySocialsProps {
  isPreview: boolean;
}

const MySocials = ({ isPreview }: MySocialsProps): JSX.Element => {
  const { t } = useTranslation();

  const billingPopup = useBillingPopup();

  const openBilling = useCallback(() => {
    billingPopup.show({
      location: PaywallLocation.SOCIAL_UPGRADE,
    });
  }, [billingPopup]);

  const { isNewFeature, setFeatureIsOld } = useNewFeature(PROFILE_VISIT_COUNT, 4);
  const { isOpen, toggleModal, close } = useModal();
  const { isOpen: isDisconnectModalOpen, toggleModal: toggleDisconnectModal, close: closeDisconnectModal } = useModal();
  const { ability } = usePermissions();
  const hasWriteAccess = ability.can('write', 'social-media');
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();
  const {
    appType,
    fetcher: {
      profile: { socialMedia },
    },
    updater: { handleInstagramAuth, deleteAllSocialData, disconnectSocialAccount, mutationLoading },
  } = useProfileViewContext();
  const isTalent = appType === AppType.Talent;

  const followerCount = socialMedia?.instagram?.metrics?.followerCount;
  const profileLink = socialMedia?.instagram?.link;
  const isInstagramConnected = socialMedia?.instagram?.isTokenValid;
  const followerLabel = t('common:profile.info.social.followerCount', {
    formattedCount: helpers.formatMetricCount(followerCount, true),
  });

  const instagramButtonLabel = useMemo(
    () => (isInstagramConnected ? followerLabel : t('talent:profileEdit.social.connectButton')),
    [isInstagramConnected, followerLabel, t]
  );

  useEffect(() => {
    if (isInstagramConnected && isTalent) {
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.TALENT_SOCIAL_FOLLOWER_COUNT_VIEW,
        data: {
          socialPlatform: SocialMediaPlatform.Instagram,
          followerCount,
          timestamp: new Date().toISOString(),
          device: deviceInfo,
          browser: browserInfo,
        },
      });
    }
  }, [followerCount, deviceInfo, browserInfo, isInstagramConnected]);

  const onConnect = useCallback(async () => {
    captureHeapEvent({
      name: HEAP_EVENT_NAMES.TALENT_SOCIAL_ADD_LINK,
      data: {
        socialPlatform: SocialMediaPlatform.Instagram,
        timestamp: new Date().toISOString(),
        device: deviceInfo,
        browser: browserInfo,
        location: 'social-upgrade',
      },
    });
    await handleInstagramAuth();
    close();
  }, [deviceInfo, browserInfo, handleInstagramAuth, close]);

  if (!isPreview && isTalent) {
    // displays the social media section on the profile edit view for talent users
    // if hasWriteAccess = true, they are able to connect/disconnect instagram
    // if hasWriteAccess = false, they have a CTA to upgrade to premium
    return (
      <>
        <SocialConnectSection
          isNewFeature={isNewFeature}
          instagramButtonLabel={instagramButtonLabel}
          profileLink={profileLink}
          hasWriteAccess={hasWriteAccess}
          toggleModal={toggleModal}
          setFeatureIsOld={setFeatureIsOld}
          toggleDisconnectModal={toggleDisconnectModal}
        />
        <InstagramConsentModal
          isOpen={isOpen}
          close={close}
          canAccess={hasWriteAccess}
          onConnect={onConnect}
          connectButtonDisabled={mutationLoading}
          openBilling={openBilling}
        />
        <InstagramDisconnectModal
          isOpen={isDisconnectModalOpen}
          close={closeDisconnectModal}
          deleteAllSocialData={deleteAllSocialData}
          disconnectSocialAccount={disconnectSocialAccount}
          loading={mutationLoading}
        />
      </>
    );
  }

  // any case besides the talent edit profile page sees the "Preview" component
  // if the profile is connected, you get a link to the instagram page
  // if the profile is not connected, nothing is rendered
  // this renders for all profile "Preview" and all public profile views
  // this also renders for the talent edit profile page in the reps app
  return (
    <Preview isInstagramConnected={isInstagramConnected} followerCount={followerCount} profileLink={profileLink} />
  );
};

export default MySocials;
